/**
 * PDP Mobile Accordion Controller
 * Handles accordion functionality for PDP sections on mobile devices only
 */
class PDPMobileAccordion {
  constructor() {
    this.accordions = [];
    this.isMobile = window.innerWidth < 768;
    this.init();
  }

  init() {
    // Only initialize on mobile
    if (!this.isMobile) {
      return;
    }

    // Find all accordion sections (each section has its own enable/disable setting)
    const accordionSections = document.querySelectorAll('[data-pdp-accordion]');
    
    accordionSections.forEach((section, index) => {
      const accordion = this.createAccordion(section, index);
      if (accordion) {
        this.accordions.push(accordion);
      }
    });

    // Only proceed if we have accordions to manage
    if (this.accordions.length === 0) {
      return;
    }

    // Handle anchor navigation
    this.handleAnchorNavigation();

    // Handle window resize
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  createAccordion(section, index) {
    const header = section.querySelector('[data-pdp-accordion-header]');
    const body = section.querySelector('[data-pdp-accordion-body]');
    const icon = header?.querySelector('[data-pdp-accordion-icon]');
    const sectionId = section.dataset.pdpAccordion;
    
    if (!header || !body) {
      return null;
    }

    // Check if this should be open by default (MORE INFO section)
    const openByDefault = section.dataset.openByDefault === 'true';
    const isOpen = openByDefault;

    // Set initial state
    this.setAccordionState(body, icon, isOpen);
    header.setAttribute('aria-expanded', isOpen);

    // Add click handler
    header.addEventListener('click', () => {
      this.toggleAccordion(section, body, icon, header);
    });

    return {
      section,
      header,
      body,
      icon,
      sectionId,
      isOpen
    };
  }

  toggleAccordion(clickedSection, clickedBody, clickedIcon, clickedHeader) {
    const wasOpen = clickedHeader.getAttribute('aria-expanded') === 'true';

    // Record the header's viewport position before any changes
    const headerRect = clickedHeader.getBoundingClientRect();
    const headerViewportTop = headerRect.top;

    // Close all other accordions instantly (no animation) so scroll position can be corrected
    this.accordions.forEach((accordion) => {
      if (accordion && accordion.section !== clickedSection && accordion.isOpen) {
        accordion.body.style.transition = 'none';
        accordion.body.style.maxHeight = '0px';
        accordion.body.style.overflow = 'hidden';
        if (accordion.icon) {
          accordion.icon.textContent = '+';
          accordion.icon.classList.remove('pdp-accordion-icon--minus');
          accordion.icon.classList.add('pdp-accordion-icon--plus');
        }
        accordion.header.setAttribute('aria-expanded', 'false');
        accordion.isOpen = false;
        // Re-enable transitions after layout
        accordion.body.offsetHeight;
        accordion.body.style.transition = '';
      }
    });

    // Correct scroll position so clicked header stays in same viewport position
    const newHeaderRect = clickedHeader.getBoundingClientRect();
    const drift = newHeaderRect.top - headerViewportTop;
    if (Math.abs(drift) > 1) {
      window.scrollBy(0, drift);
    }

    // Toggle clicked accordion (with animation)
    const newState = !wasOpen;
    this.setAccordionState(clickedBody, clickedIcon, newState);
    clickedHeader.setAttribute('aria-expanded', newState);

    // Update accordion state
    const accordion = this.accordions.find(acc => acc.section === clickedSection);
    if (accordion) {
      accordion.isOpen = newState;
    }

  }

  setAccordionState(body, icon, isOpen) {
    if (!body) return;

    if (isOpen) {
      // Animate open: set max-height to scrollHeight for the transition,
      // then remove the cap so dynamically expanding content (e.g. clicking
      // an ingredient detail inside the accordion) is never clipped.
      body.style.maxHeight = `${body.scrollHeight}px`;
      body.style.overflow = 'hidden';
      const onOpen = () => {
        // Only uncap if the accordion is still open
        if (body.style.maxHeight !== '0px') {
          body.style.maxHeight = 'none';
          body.style.overflow = 'visible';
        }
        body.removeEventListener('transitionend', onOpen);
      };
      body.addEventListener('transitionend', onOpen);
      if (icon) {
        icon.textContent = '-';
        icon.classList.remove('pdp-accordion-icon--plus');
        icon.classList.add('pdp-accordion-icon--minus');
      }
    } else {
      // Animate closed: if currently uncapped, snapshot the height first
      // so the CSS transition has a numeric starting value.
      if (body.style.maxHeight === 'none') {
        body.style.maxHeight = `${body.scrollHeight}px`;
        body.style.overflow = 'hidden';
        // Force layout so the browser registers the starting value
        body.offsetHeight; // eslint-disable-line no-unused-expressions
      }
      body.style.maxHeight = '0';
      body.style.overflow = 'hidden';
      if (icon) {
        icon.textContent = '+';
        icon.classList.remove('pdp-accordion-icon--minus');
        icon.classList.add('pdp-accordion-icon--plus');
      }
    }
  }

  scrollToAccordion(section) {
    const header = document.querySelector('store-header');
    const headerHeight = header ? header.offsetHeight : 0;
    
    const rect = section.getBoundingClientRect();
    const scrollY = window.scrollY + rect.top - headerHeight - 10;
    
    window.scrollTo({
      top: Math.max(scrollY, 0),
      behavior: 'smooth'
    });
  }

  handleAnchorNavigation() {
    // Handle hash changes
    window.addEventListener('hashchange', () => {
      this.openAccordionFromHash();
    });

    // Handle initial hash on page load
    if (window.location.hash) {
      setTimeout(() => {
        this.openAccordionFromHash();
      }, 100);
    }

    // Handle clicks on anchor links
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href^="#"]');
      if (link && link.href.includes('#')) {
        const hash = link.href.split('#')[1];
        const targetSection = document.querySelector(`[data-pdp-accordion="${hash}"]`);
        if (targetSection) {
          e.preventDefault();
          const accordion = this.accordions.find(acc => acc.section === targetSection);
          if (accordion && !accordion.isOpen) {
            accordion.header.click();
          }
          setTimeout(() => {
            this.scrollToAccordion(targetSection);
          }, 100);
        }
      }
    });
  }

  openAccordionFromHash() {
    const hash = window.location.hash.substring(1);
    if (!hash) return;

    const targetSection = document.querySelector(`[data-pdp-accordion="${hash}"]`);
    if (targetSection) {
      const accordion = this.accordions.find(acc => acc.section === targetSection);
      if (accordion && !accordion.isOpen) {
        accordion.header.click();
      }
    }
  }

  handleResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 768;

    // If switching from mobile to desktop or vice versa, reinitialize
    if (wasMobile !== this.isMobile) {
      // Reset all accordions
      this.accordions.forEach((accordion) => {
        if (accordion && accordion.body) {
          accordion.body.style.maxHeight = '';
          accordion.body.style.overflow = '';
        }
      });

      // Reinitialize if mobile
      if (this.isMobile) {
        this.init();
      }
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PDPMobileAccordion();
  });
} else {
  new PDPMobileAccordion();
}
